function initScrollAnimations(root = document) {
  const elements = root.querySelectorAll('[data-animate]');

  if (!elements.length) return;

  // respect reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    elements.forEach(el => el.classList.add('is-inview'));
    return;
  }

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;

      const el = entry.target;

      el.classList.add('is-inview');
      obs.unobserve(el);
    });
  }, {
    threshold: 0.15
  });

  elements.forEach(el => observer.observe(el));
}

// initial load
initScrollAnimations();

class HeaderElement extends HTMLElement {
  constructor() {
    super();
    this.scrollClass = 'header--scrolled';
    this.threshold = 20;
    this.ticking = false;
  }

  connectedCallback() {
    this.header = document.querySelector('.header, header');
    if (!this.header) return;
    this.init();
  }

  init() {
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.update();
  }

  onScroll() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  update() {
    if (!this.header) return;
    if (window.scrollY > this.threshold) {
      this.header.classList.add(this.scrollClass);
    } else {
      this.header.classList.remove(this.scrollClass);
    }
  }
}

customElements.define('header-element', HeaderElement);

class ContactForm extends HTMLElement {
  connectedCallback() {
    this.initTabs();
  }

  initTabs() {
    const tabs = this.querySelectorAll('.contact-form__tab[data-form-id]');
    const panels = this.querySelectorAll('.contact-form__panel');
    if (!tabs.length || !panels.length) return;

    // Wrap panels in one container so we can lay out tabs | panels
    const firstPanel = panels[0];
    const panelsWrapper = document.createElement('div');
    panelsWrapper.className = 'contact-form__panels';
    firstPanel.parentNode.insertBefore(panelsWrapper, firstPanel);
    panels.forEach((p) => panelsWrapper.appendChild(p));

    const showForm = (formId) => {
      tabs.forEach((t) => {
        const isSelected = t.getAttribute('data-form-id') === formId;
        t.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        t.classList.toggle('is-active', isSelected);
      });
      panels.forEach((p) => {
        p.hidden = p.getAttribute('data-form-id') !== formId;
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        showForm(tab.getAttribute('data-form-id'));
      });
    });

    // First tab and its form selected by default
    const defaultFormId = tabs[0].getAttribute('data-form-id');
    if (defaultFormId) showForm(defaultFormId);
  }
}

customElements.define('contact-form', ContactForm);

class MenuMobile extends HTMLElement {
    constructor(){
        super();
        this.element = this;
        this.mobile_menu = this.element.querySelector(".menu__mobile");
        this.drawer = this.querySelector(".menu__mobile--drawer");
        this.overlay = this.querySelector(".menu__mobile--overlay");

        this.header = document.querySelector(".header__content");
        document.documentElement.style.setProperty(
        "--header-height",
        this.header.clientHeight + "px"
        );

        this.bindEvents();
    }

    closeMenu() {
        if (!this.classList.contains("is-open")) return;
        this.classList.remove("is-open");
        this.updateOverlay(false);
        if (this.drawer && window.bodyScrollLock) {
            bodyScrollLock.enableBodyScroll(this.drawer);
        }
    }

    updateOverlay(visible) {
        if (!this.overlay) return;
        this.overlay.classList.toggle("is-visible", visible);
        this.overlay.setAttribute("aria-hidden", !visible);
    }

    bindEvents(){
        if (this.mobile_menu) {
            this.mobile_menu.addEventListener("click", this.toggleMenuMobile.bind(this));
        }
        if (this.overlay) {
            this.overlay.addEventListener("click", () => this.closeMenu());
        }
        if (this.drawer) {
            this.drawer.addEventListener("click", (e) => {
                const link = e.target.closest("a");
                if (!link) return;
                if (link.getAttribute("href") === "#" || (link.getAttribute("href") || "").startsWith("#")) {
                    e.preventDefault();
                }
                this.closeMenu();
            });
        }
    }

    toggleMenuMobile(){
        const isOpen = this.classList.toggle("is-open");
        this.updateOverlay(isOpen);

        if (!this.drawer || !window.bodyScrollLock) return;

        if (isOpen) {
            bodyScrollLock.disableBodyScroll(this.drawer);
        } else {
            bodyScrollLock.enableBodyScroll(this.drawer);
        }
    }
}

customElements.define("menu-mobile", MenuMobile);

class AccordingElement extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector(".accordion__button");
    this.content = this.querySelector(".accordion__content");

    if (!this.button || !this.content) return;

    this.onToggle = this.onToggle.bind(this);
  }

  connectedCallback() {
    this.button.addEventListener("click", this.onToggle);
  }

  disconnectedCallback() {
    this.button.removeEventListener("click", this.onToggle);
  }

  onToggle() {
    const isOpen = this.button.getAttribute("aria-expanded") === "true";

    // zatvori sve ostale
    AccordingElement.closeAllExcept(this);

    // toggle ovog
    this.setOpen(!isOpen);
  }

  setOpen(open) {
    this.button.setAttribute("aria-expanded", open ? "true" : "false");
    this.content.setAttribute("aria-hidden", open ? "false" : "true");
  }

  close() {
    this.setOpen(false);
  }

  static closeAllExcept(current) {
    const all = document.querySelectorAll("according-element");

    all.forEach((el) => {
      if (el !== current && typeof el.close === "function") {
        el.close();
      }
    });
  }
}

customElements.define("according-element", AccordingElement);

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
      button: this.querySelector('button'),
      panel: this.querySelector('ul'),
    };
    if (this.elements.button) {
      this.elements.button.addEventListener('click', this.openSelector.bind(this));
      this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
    }
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
    this.querySelectorAll('a[data-action="localization-form-submit"]').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));
  }

  hidePanel() {
    if (this.elements.button) this.elements.button.setAttribute('aria-expanded', 'false');
    if (this.elements.panel) this.elements.panel.setAttribute('hidden', true);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    this.hidePanel();
    if (this.elements.button) this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    if (this.elements.input) this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    if (!this.elements.button || !this.elements.panel) return;
    this.elements.button.focus();
    this.elements.panel.toggleAttribute('hidden');
    this.elements.button.setAttribute('aria-expanded', (this.elements.button.getAttribute('aria-expanded') === 'false').toString());
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }
}

customElements.define('localization-form', LocalizationForm);

class ArticleCursorTooltip extends HTMLElement {
  static offsetX = 12;
  static offsetY = 12;

  constructor() {
    super();
    this._lastX = 0;
    this._lastY = 0;
    this._boundOnMouseMove = this._onDocumentMouseMove.bind(this);
    this._cards = [];
  }

  connectedCallback() {
    this._cards = Array.from(this.querySelectorAll('.article'));
    if (!this._cards.length) return;

    document.addEventListener('mousemove', this._boundOnMouseMove, { passive: true });

    this._cards.forEach((card) => this._attachCard(card));
  }

  disconnectedCallback() {
    document.removeEventListener('mousemove', this._boundOnMouseMove);
    this._cards.forEach((card) => this._detachCard(card));
    this._cards = [];
  }

  _onDocumentMouseMove(e) {
    this._lastX = e.clientX;
    this._lastY = e.clientY;
  }

  _attachCard(card) {
    const label = card.querySelector('.article-read-more');
    if (!label) return;

    const onEnter = () => {
      label.style.left = `${this._lastX + ArticleCursorTooltip.offsetX}px`;
      label.style.top = `${this._lastY + ArticleCursorTooltip.offsetY}px`;
      label.style.opacity = '1';
    };
    const onMove = (e) => {
      label.style.left = `${e.clientX + ArticleCursorTooltip.offsetX}px`;
      label.style.top = `${e.clientY + ArticleCursorTooltip.offsetY}px`;
    };
    const onLeave = () => {
      label.style.opacity = '0';
    };

    card.addEventListener('mouseenter', onEnter);
    card.addEventListener('mousemove', onMove);
    card.addEventListener('mouseleave', onLeave);

    card._articleCursorTooltip = { onEnter, onMove, onLeave };
  }

  _detachCard(card) {
    const ref = card._articleCursorTooltip;
    if (!ref) return;
    card.removeEventListener('mouseenter', ref.onEnter);
    card.removeEventListener('mousemove', ref.onMove);
    card.removeEventListener('mouseleave', ref.onLeave);
    delete card._articleCursorTooltip;
  }
}

customElements.define('article-cursor-tooltip', ArticleCursorTooltip);